import { useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'motion/react'
import { CheckCircle, AlertTriangle, Info, XCircle, X } from 'lucide-react'
import { cn } from '@utils'
import { useUIStore, UIMessage } from '@/stores/useUIStore'

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_VISIBLE = 3
const TOAST_HEIGHT = 80
const PEEK = 12
const EXPANDED_GAP = 8

// Default titles per type
const DEFAULT_TITLE: Record<UIMessage['type'], string> = {
  success: 'Success',
  error: 'Something went wrong',
  warning: 'Heads up',
  info: 'Info',
}

// Per-type: card bg, icon circle bg, progress bar color
const TYPE: Record<UIMessage['type'], { card: string; circle: string; progress: string }> = {
  success: {
    card: 'bg-green-950',
    circle: 'bg-green-600',
    progress: 'bg-green-600',
  },
  error: { card: 'bg-red-950', circle: 'bg-red-700', progress: 'bg-red-700' },
  warning: {
    card: 'bg-amber-950',
    circle: 'bg-amber-700',
    progress: 'bg-amber-700',
  },
  info: { card: 'bg-blue-950', circle: 'bg-blue-700', progress: 'bg-blue-700' },
}

const ICON = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
}

// ─── Progress Bar ─────────────────────────────────────────────────────────────

const ToastProgress = ({ type, persistent }: { type: UIMessage['type']; persistent?: boolean }) => {
  if (persistent) {
    return null
  }
  const duration = type === 'error' ? 7 : 5
  return (
    <motion.div
      className={cn('absolute bottom-0 left-0 h-[2px] rounded-bl-[20px]', TYPE[type].progress)}
      initial={{ width: '100%' }}
      animate={{ width: '0%' }}
      transition={{ duration, ease: 'linear' }}
    />
  )
}

// ─── Single Toast ─────────────────────────────────────────────────────────────

const Toast = ({ toast, onClose }: { toast: UIMessage; onClose: (id: string) => void }) => {
  const t = TYPE[toast.type]
  const Icon = ICON[toast.type]
  const title = toast.title || DEFAULT_TITLE[toast.type]

  return (
    <div
      className={cn(
        'relative flex items-center gap-3',
        'w-[400px] max-w-[calc(100vw-2rem)]',
        'rounded-[50px] px-4 py-3.5 overflow-hidden',
        t.card,
        'border border-white/[0.07]',
        'shadow-[0_8px_32px_rgba(0,0,0,0.5),0_2px_8px_rgba(0,0,0,0.4)]'
      )}>
      {/* Colored circular icon badge */}
      <div className={cn('shrink-0 flex items-center justify-center', 'w-10 h-10 rounded-full', t.circle)}>
        <Icon size={19} className="text-white" strokeWidth={2.2} />
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white leading-tight m-0">{title}</p>
        <p
          className="text-sm text-white/60 leading-snug m-0 mt-0.5"
          dangerouslySetInnerHTML={{ __html: toast.message }}
        />
      </div>

      {/* Close */}
      <button
        type="button"
        onClick={() => onClose(toast.id)}
        aria-label="Dismiss"
        className={cn(
          'shrink-0 flex items-center justify-center self-start',
          'w-7 h-7 rounded-full p-0',
          'bg-white/12 border border-transparent cursor-pointer',
          'text-white/60',
          'transition-all duration-150',
          'hover:bg-white/18 hover:text-white hover:border-white/40'
        )}>
        <X size={13} strokeWidth={2.5} />
      </button>

      <ToastProgress type={toast.type} persistent={toast.persistent} />
    </div>
  )
}

// ─── Stack Container ──────────────────────────────────────────────────────────

export const ToastContainer = () => {
  const uiMessages = useUIStore(state => state.uiMessages)
  const removeUIMessage = useUIStore(state => state.removeUIMessage)
  const [hovered, setHovered] = useState(false)

  const visible = uiMessages.slice(-MAX_VISIBLE)
  const hasStack = visible.length > 1
  const expanded = hovered && hasStack

  const maxHeight = MAX_VISIBLE * TOAST_HEIGHT + (MAX_VISIBLE - 1) * EXPANDED_GAP

  return createPortal(
    <div
      className={cn(
        'fixed bottom-4 right-4 z-9999',
        'w-[400px] max-w-[calc(100vw-2rem)]',
        visible.length === 0 ? 'pointer-events-none' : 'pointer-events-auto'
      )}
      style={{ height: maxHeight }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      <AnimatePresence initial={false}>
        {visible.map((toast, i) => {
          const fromEnd = visible.length - 1 - i
          const yPos = expanded ? -(fromEnd * (TOAST_HEIGHT + EXPANDED_GAP)) : -(fromEnd * PEEK)
          const scale = expanded || fromEnd === 0 ? 1 : 1 - fromEnd * 0.055
          const opacity = expanded || fromEnd === 0 ? 1 : 1 - fromEnd * 0.3

          return (
            <motion.div
              key={toast.id}
              className="absolute bottom-0 right-0 w-full origin-bottom"
              initial={{ opacity: 0, y: 24, scale: 0.92 }}
              animate={{
                opacity,
                y: yPos,
                scale,
                zIndex: MAX_VISIBLE - fromEnd,
              }}
              exit={{
                opacity: 0,
                y: 24,
                scale: 0.92,
                transition: { duration: 0.18, ease: 'easeIn' },
              }}
              transition={{ type: 'spring', stiffness: 380, damping: 30 }}>
              <Toast toast={toast} onClose={removeUIMessage} />
            </motion.div>
          )
        })}
      </AnimatePresence>

      {/* Count badge */}
      <AnimatePresence>
        {hasStack && !expanded && (
          <motion.div
            className="absolute right-3 z-4 bg-black/70 backdrop-blur-md text-white text-[11px] font-semibold leading-none px-2.5 py-1.5 rounded-full pointer-events-none shadow-lg border border-white/10"
            style={{ bottom: TOAST_HEIGHT - 10 }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}>
            {visible.length}
          </motion.div>
        )}
      </AnimatePresence>
    </div>,
    document.body
  )
}

export default ToastContainer
